import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useRegistrarAlta } from "@/hooks/useHospitalization";
import { toast } from "sonner";

const makeSchema = (t: (k: string) => string) =>
  z.object({ motivo: z.string().optional() });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

interface DischargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hospitalizationId: string;
  petNome: string;
}

export function DischargeDialog({ open, onOpenChange, hospitalizationId, petNome }: DischargeDialogProps) {
  const { t } = useTranslation("hospitalization");
  const altaMutation = useRegistrarAlta();

  const schema = makeSchema(t);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { motivo: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await altaMutation.mutateAsync({ id: hospitalizationId, motivo: values.motivo });
      toast.success(t("success.altaRegistrada"));
      onOpenChange(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("alta.confirmTitle", { nome: petNome })}</AlertDialogTitle>
          <AlertDialogDescription>{t("alta.confirmDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="motivo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("alta.motivo")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("alta.motivoPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel>{t("alta.cancelar")}</AlertDialogCancel>
              <Button type="submit" disabled={altaMutation.isPending}>
                {t("alta.confirmar")}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
